import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plug, CheckCircle2, ExternalLink, AlertCircle, RefreshCw, Trash2 } from "lucide-react";

const PLATFORMS = [
  {
    key: "fanvue",
    label: "Fanvue",
    color: "emerald",
    type: "oauth",
    note: "OAuth 2.0 + PKCE cez Supabase Edge Function (FANVUE_CLIENT_ID & FANVUE_CLIENT_SECRET v secrets).",
    docs: "https://api.fanvue.com/docs/app-store/introduction",
  },
  {
    key: "telegram",
    label: "Telegram",
    color: "sky",
    type: "token",
    placeholder: "123456:ABC-DEF... (bot token from @BotFather)",
    docs: "https://core.telegram.org/bots#how-do-i-create-a-bot",
  },
];

export default function Connections() {
  const { t } = useT();
  const [params] = useSearchParams();
  const [conns, setConns] = useState([]);
  const [tokens, setTokens] = useState({});

  const load = async () => {
    const r = await api.get("/connections");
    setConns(r.data);
  };
  useEffect(() => {
    load();
    if (params.get("fanvue") === "connected") {
      toast.success("Fanvue connected ✨");
      // Clean URL
      window.history.replaceState({}, "", "/connections");
    }
    const fanvueErr = params.get("fanvue_error");
    if (fanvueErr) {
      toast.error("Fanvue: " + fanvueErr, { duration: 10000 });
      window.history.replaceState({}, "", "/connections");
    }
  }, []);

  const isConnected = (key) => conns.some((c) => c.platform === key);
  const username = (key) => conns.find((c) => c.platform === key)?.username;

  const saveSimple = async (platform) => {
    const token = tokens[platform];
    if (!token) return;
    try {
      const r = await api.post("/connections/simple", { platform, token });
      toast.success(`${platform} connected ${r.data.username || ""}`);
      setTokens({ ...tokens, [platform]: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const startFanvueOAuth = async () => {
    try {
      const r = await api.get("/connections/fanvue/start");
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "FANVUE_CLIENT_ID not configured");
    }
  };

  const disconnect = async (key) => {
    await api.delete(`/connections/${key}`);
    toast.success(`${key} disconnected`);
    load();
  };

  const [syncing, setSyncing] = useState(false);
  const syncFanvue = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/connections/fanvue/sync");
      (r.data.note
        ? toast.warning(`Sync: ${r.data.fans ?? 0} fans, ${r.data.messages ?? 0} správ · problém: ${r.data.note}`, { duration: 10000 })
        : toast.success(`Sync: ${r.data.fans ?? 0} fanúšikov, ${r.data.messages ?? 0} správ`));
    } catch (e) {
      toast.error("Sync failed: " + (e?.response?.data?.detail || e.message), { duration: 8000 });
    } finally {
      setSyncing(false);
    }
  };

  const clearDemo = async () => {
    if (!window.confirm("Vymazať všetkých demo fanov a ich konverzácie? Reálne pripojené účty zostanú.")) return;
    const r = await api.post("/dev/clear-demo");
    toast.success(`Cleared ${r.data.deleted_fans} demo fans, ${r.data.deleted_messages} messages`);
  };

  return (
    <div className="p-8 lg:p-12 max-w-5xl fade-up">
      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.35em] text-white/40">{t("connections")}</div>
        <h1 className="font-serif text-5xl mt-2">Plug her into <span className="italic text-fuchsia-400">everything</span>.</h1>
      </div>

      <div className="space-y-4">
        {PLATFORMS.map((p) => {
          const connected = isConnected(p.key);
          return (
            <div key={p.key} className="bg-[rgb(18,20,28)] border border-white/5 p-6" data-testid={`conn-${p.key}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                  <div className={`w-1.5 h-12 ${
                    p.color === "emerald" ? "bg-emerald-400" :
                    p.color === "sky" ? "bg-sky-400" :
                    p.color === "rose" ? "bg-rose-400" : "bg-white"
                  }`} />
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="font-serif text-2xl">{p.label}</h3>
                      {connected ? (
                        <span className="text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1">
                          <CheckCircle2 size={11} /> {t("connected")} {username(p.key) && `· ${username(p.key)}`}
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest text-white/30 flex items-center gap-1">
                          <AlertCircle size={11} /> {t("not_connected")}
                        </span>
                      )}
                    </div>
                    {p.note && <p className="text-xs text-white/40 mt-1.5 max-w-md">{p.note}</p>}
                    <a
                      href={p.docs}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] uppercase tracking-widest text-fuchsia-300 hover:text-fuchsia-200 inline-flex items-center gap-1 mt-1"
                      data-testid={`docs-${p.key}`}
                    >
                      docs <ExternalLink size={9} />
                    </a>
                  </div>
                </div>
                {connected && (
                  <div className="flex gap-2">
                    {p.key === "fanvue" && (
                      <Button
                        onClick={syncFanvue}
                        disabled={syncing}
                        data-testid="btn-sync-fanvue"
                        className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-none text-xs uppercase tracking-widest"
                      >
                        <RefreshCw size={13} className={`mr-2 ${syncing ? "animate-spin" : ""}`} />
                        {syncing ? "Syncing…" : "Sync now"}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => disconnect(p.key)}
                      data-testid={`btn-disconnect-${p.key}`}
                      className="bg-transparent border-white/10 text-white/70 hover:bg-rose-500/10 hover:text-rose-300 hover:border-rose-500/30 rounded-none text-xs uppercase tracking-widest"
                    >
                      {t("disconnect")}
                    </Button>
                  </div>
                )}
              </div>

              {!connected && (
                <div className="mt-5">
                  {p.type === "oauth" ? (
                    <Button
                      onClick={startFanvueOAuth}
                      data-testid={`btn-connect-${p.key}`}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-none text-xs uppercase tracking-widest px-5"
                    >
                      <Plug size={14} className="mr-2" />
                      {t("connect")} {p.label}
                    </Button>
                  ) : (
                    <div className="flex gap-2 flex-wrap">
                      <Input
                        value={tokens[p.key] || ""}
                        onChange={(e) => setTokens({ ...tokens, [p.key]: e.target.value })}
                        placeholder={p.placeholder}
                        data-testid={`input-token-${p.key}`}
                        className="bg-transparent border-white/10 rounded-none flex-1 min-w-[280px] font-mono text-xs"
                      />
                      <Button
                        onClick={() => saveSimple(p.key)}
                        data-testid={`btn-connect-${p.key}`}
                        className="bg-white text-black hover:bg-white/90 rounded-none text-xs uppercase tracking-widest px-5"
                      >
                        {t("connect")}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
